import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Eye,
  GripVertical,
  MessageSquare,
  Plus,
  Trash2,
  User,
} from 'lucide-react'
import DOMPurify from 'dompurify'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'

import { BaseTextInput } from 'components/FormUtils/TextInput'
import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'

import {
  CreateSequencePayload,
  UpdateSequencePayload,
  useCreateSequence,
  useGetCampaignSequence,
  useUpdateSequence,
} from '../sequences_api'
import {
  type CampaignLeadReviewItem,
  useGetCampaignLeadReview,
} from '../campaigns_api'
import { SequenceForm, defaultStep } from '../SequenceTab/SequenceStepFormItem'
import { EmailBodyEditor } from '../SequenceTab/EmailBodyEditor'
import { handleFormError } from 'utils/handleFormError'

import { LiveCampaignBanner } from './LiveCampaignBanner'

interface WizardSequenceStepProps {
  campaignId: number
  campaignName: string
  onNext: () => void
  onBack: () => void
  stepNumber: number
  totalSteps: number
  readOnly?: boolean
  isLiveCampaign?: boolean
  backLabel?: string
}

interface PreviewContact {
  id: string
  label: string
  subtitle: string
  tokens: Record<string, string>
}

type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error'

const emptyDefaultValues: SequenceForm = {
  title: '',
  steps: [{ ...defaultStep }],
}

const SAMPLE_PREVIEW_CONTACT: PreviewContact = {
  id: 'sample-preview-contact',
  label: 'Sample Contact',
  subtitle: 'Example Company · Decision Maker',
  tokens: {
    FIRST_NAME: 'Alex',
    LAST_NAME: 'Morgan',
    EMAIL: 'alex@examplecompany.com',
    COMPANY: 'Example Company',
    TITLE: 'Decision Maker',
  },
}

function getApiErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) {
    return 'Unable to save sequence. Please try again.'
  }

  const detail = err.response?.data?.detail
  if (typeof detail === 'string' && detail.length > 0) {
    return detail
  }

  return 'Unable to save sequence. Please try again.'
}

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function fillVariables(
  text: string,
  tokens: Record<string, string>,
  options?: { escapeValues?: boolean }
): string {
  let result = text
  Object.entries(tokens).forEach(([key, value]) => {
    const replacement = options?.escapeValues ? escapeHtml(value) : value
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), replacement)
  })
  return result
}

function detectUnfilledVariables(text: string): string[] {
  return Array.from(new Set(text.match(/\{[A-Z_]+\}/g) ?? []))
}

function toPreviewContact(contact: CampaignLeadReviewItem): PreviewContact {
  const [firstName = 'Contact', ...rest] = (contact.full_name || '')
    .trim()
    .split(' ')
    .filter(Boolean)
  const lastName = rest.join(' ')
  return {
    id: `contact-${contact.contact_id}`,
    label: `${firstName} ${lastName}`.trim(),
    subtitle: [contact.company_name, contact.title].filter(Boolean).join(' · '),
    tokens: {
      FIRST_NAME: firstName,
      LAST_NAME: lastName,
      EMAIL: contact.email || '',
      COMPANY: contact.company_name || '',
      TITLE: contact.title || '',
    },
  }
}

export function WizardSequenceStep({
  campaignId,
  campaignName,
  onNext,
  onBack,
  stepNumber,
  totalSteps,
  readOnly = false,
  isLiveCampaign = false,
  backLabel = 'Back',
}: WizardSequenceStepProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [expandedStepIndex, setExpandedStepIndex] = useState(0)
  const [previewStepIndex, setPreviewStepIndex] = useState(0)
  const [previewContactId, setPreviewContactId] = useState<string>('')
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hydratedSequenceIdRef = useRef<number | null>(null)
  const lastSavedSnapshotRef = useRef<string | null>(null)
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true))
  const latestDraftValuesRef = useRef<SequenceForm>(emptyDefaultValues)

  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)
  const { data: leadReviewData } = useGetCampaignLeadReview(campaignId)

  const createSequenceMutation = useCreateSequence()
  const updateSequenceMutation = useUpdateSequence(campaignId)

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<SequenceForm>({
    defaultValues: emptyDefaultValues,
    mode: 'onChange',
  })
  const watchedTitle = watch('title') ?? ''

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'steps',
    keyName: 'fieldKey',
  })

  const watchedStepsValue = watch('steps')
  const watchedSteps = useMemo(
    () => watchedStepsValue ?? [],
    [watchedStepsValue]
  )

  const previewContacts = useMemo(() => {
    const includedContacts =
      leadReviewData?.results?.filter((contact) => contact.is_included) ?? []
    if (includedContacts.length > 0) {
      return includedContacts.slice(0, 50).map(toPreviewContact)
    }
    if ((leadReviewData?.summary.total ?? 0) === 0) {
      return [SAMPLE_PREVIEW_CONTACT]
    }
    return []
  }, [leadReviewData])

  const hasCampaignContacts = (leadReviewData?.summary.total ?? 0) > 0
  const hasIncludedPreviewContacts =
    (leadReviewData?.results?.some((contact) => contact.is_included) ??
      false) === true
  const isUsingSamplePreview = !hasCampaignContacts
  const showNoIncludedContactsState =
    hasCampaignContacts && !hasIncludedPreviewContacts

  const activePreviewContact = useMemo(() => {
    if (showNoIncludedContactsState) {
      return null
    }
    return (
      previewContacts.find((contact) => contact.id === previewContactId) ??
      previewContacts[0]
    )
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  const safePreviewStepIndex = Math.min(
    previewStepIndex,
    Math.max(0, watchedSteps.length - 1)
  )
  const previewStep = watchedSteps[safePreviewStepIndex]

  const filledPreviewSubject = previewStep
    ? fillVariables(
        previewStep.email_subject || '',
        activePreviewContact?.tokens ?? {}
      )
    : ''
  const filledPreviewBody = previewStep
    ? fillVariables(
        previewStep.email_body || '',
        activePreviewContact?.tokens ?? {},
        { escapeValues: true }
      )
    : ''
  const previewWarnings = useMemo(
    () =>
      Array.from(
        new Set([
          ...detectUnfilledVariables(filledPreviewSubject),
          ...detectUnfilledVariables(filledPreviewBody),
        ])
      ),
    [filledPreviewBody, filledPreviewSubject]
  )

  const currentDraftValues = useMemo<SequenceForm>(
    () => ({
      title: watchedTitle,
      steps: watchedSteps.map((step) => ({
        id: step.id,
        email_subject: step.email_subject ?? '',
        email_body: step.email_body ?? '',
        wait_in_days: step.wait_in_days ?? defaultStep.wait_in_days,
      })),
    }),
    [watchedSteps, watchedTitle]
  )

  const normalizeValues = useCallback(
    (values: SequenceForm) => {
      const normalizedTitle = values.title.trim() || campaignName.trim()
      return {
        title: normalizedTitle,
        steps: values.steps.map((step, index) => ({
          id: step.id,
          order: index + 1,
          email_subject: step.email_subject,
          email_body: step.email_body,
          wait_in_days: Math.max(1, step.wait_in_days),
        })),
      }
    },
    [campaignName]
  )

  const serializeValues = useCallback(
    (values: SequenceForm) =>
      JSON.stringify({
        sequenceId: sequence?.id ?? null,
        ...normalizeValues(values),
      }),
    [normalizeValues, sequence?.id]
  )

  const canSaveValues = (values: SequenceForm) =>
    values.steps.length > 0 &&
    values.steps.every(
      (step) =>
        step.email_subject?.trim().length > 0 &&
        stripHtml(step.email_body || '').length > 0
    )

  const currentDraftSnapshot = serializeValues(currentDraftValues)
  const currentDraftHasAnyContent =
    currentDraftValues.title.trim().length > 0 ||
    currentDraftValues.steps.some(
      (step) => step.email_subject || step.email_body
    )
  const currentDraftCanSave = canSaveValues(currentDraftValues)

  useEffect(() => {
    latestDraftValuesRef.current = currentDraftValues
  }, [currentDraftValues])

  useEffect(() => {
    if (sequence && hydratedSequenceIdRef.current !== sequence.id) {
      const hydratedValues = {
        title: sequence.title || campaignName,
        steps: sequence.steps
          .sort((a, b) => a.order - b.order)
          .map((step) => ({
            id: step.id,
            email_subject: step.email_subject,
            email_body: step.email_body,
            wait_in_days: step.wait_in_days,
          })),
      }
      hydratedSequenceIdRef.current = sequence.id
      reset({
        title: hydratedValues.title,
        steps: hydratedValues.steps,
      })
      lastSavedSnapshotRef.current = serializeValues(hydratedValues)
      setSaveStatus('saved')
      setExpandedStepIndex(0)
      setPreviewStepIndex(0)
    }
  }, [campaignName, reset, sequence, serializeValues])

  useEffect(() => {
    if (sequence || !campaignName.trim()) return
    if (getValues('title').trim()) return

    setValue('title', campaignName, {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [campaignName, getValues, sequence, setValue])

  useEffect(() => {
    if (showNoIncludedContactsState || !previewContacts.length) {
      return
    }
    const hasCurrentSelection = previewContacts.some(
      (contact) => contact.id === previewContactId
    )
    if (!previewContactId || !hasCurrentSelection) {
      setPreviewContactId(previewContacts[0].id)
    }
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  const enqueueSave = useCallback(
    (
      values: SequenceForm,
      options?: { silent?: boolean }
    ): Promise<boolean> => {
      if (readOnly) {
        return Promise.resolve(true)
      }

      const snapshot = serializeValues(values)
      if (lastSavedSnapshotRef.current === snapshot) {
        return Promise.resolve(true)
      }

      saveChainRef.current = saveChainRef.current
        .catch(() => true)
        .then(async () => {
          setSaveStatus('saving')

          if (!options?.silent) {
            setIsSaving(true)
          }

          try {
            const normalized = normalizeValues(values)

            if (sequence) {
              const payload: UpdateSequencePayload = {
                title: normalized.title,
                steps: normalized.steps,
              }

              const [err] = await to(
                updateSequenceMutation.mutateAsync({
                  sequenceId: sequence.id,
                  payload,
                })
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            } else {
              const payload: CreateSequencePayload = {
                campaign_id: campaignId,
                title: normalized.title,
                steps: normalized.steps.map(
                  ({ order, email_subject, email_body, wait_in_days }) => ({
                    order,
                    email_subject,
                    email_body,
                    wait_in_days,
                  })
                ),
              }

              const [err] = await to(
                createSequenceMutation.mutateAsync(payload)
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            }

            lastSavedSnapshotRef.current = snapshot
            setSaveStatus('saved')
            return true
          } finally {
            if (!options?.silent) {
              setIsSaving(false)
            }
          }
        })

      return saveChainRef.current
    },
    [
      campaignId,
      createSequenceMutation,
      normalizeValues,
      readOnly,
      sequence,
      serializeValues,
      setError,
      updateSequenceMutation,
    ]
  )

  useEffect(() => {
    if (readOnly) {
      setSaveStatus('idle')
      return
    }

    if (!currentDraftHasAnyContent) {
      setSaveStatus(sequence ? 'saved' : 'idle')
      return
    }

    const lastSavedSnapshot = lastSavedSnapshotRef.current

    if (lastSavedSnapshot === currentDraftSnapshot) {
      setSaveStatus((current) => (current === 'saving' ? current : 'saved'))
      return
    }

    setSaveStatus('unsaved')

    if (!currentDraftCanSave) {
      return
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }

    autosaveTimerRef.current = setTimeout(() => {
      void enqueueSave(latestDraftValuesRef.current, { silent: true })
    }, 1800)

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [
    currentDraftCanSave,
    currentDraftHasAnyContent,
    currentDraftSnapshot,
    enqueueSave,
    readOnly,
    sequence,
  ])

  const handlePrimarySubjectChange = (subject: string) => {
    setValue('steps.0.email_subject', subject, {
      shouldDirty: true,
      shouldValidate: true,
    })

    const currentSteps = getValues('steps')
    currentSteps.forEach((step, index) => {
      if (index === 0) return
      const existingSubject = step.email_subject?.trim() ?? ''
      if (existingSubject === '' || existingSubject.startsWith('Re: ')) {
        setValue(
          `steps.${index}.email_subject`,
          subject.trim() ? `Re: ${subject}` : '',
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        )
      }
    })
  }

  const addStep = () => {
    const firstSubject = getValues('steps.0.email_subject') ?? ''
    const nextIndex = watchedSteps.length
    append({
      ...defaultStep,
      wait_in_days: nextIndex === 0 ? defaultStep.wait_in_days : 3,
      email_subject: firstSubject.trim() ? `Re: ${firstSubject}` : '',
    })
    setExpandedStepIndex(nextIndex)
    setPreviewStepIndex(nextIndex)
  }

  const duplicateStep = (index: number) => {
    const step = getValues(`steps.${index}`)
    insert(index + 1, {
      email_subject: step.email_subject,
      email_body: step.email_body,
      wait_in_days: step.wait_in_days,
    })
    setExpandedStepIndex(index + 1)
    setPreviewStepIndex(index + 1)
  }

  const removeStepAtIndex = (index: number) => {
    remove(index)
    setExpandedStepIndex((current) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    })
    setPreviewStepIndex((current) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    })
  }

  const onSubmit = handleSubmit(async (values) => {
    if (readOnly) {
      onNext()
      return
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }
    const success = await enqueueSave(values)
    if (success) {
      onNext()
    }
  })

  const canProceed =
    !readOnly &&
    isValid &&
    watchedSteps.length > 0 &&
    watchedSteps.every(
      (step) =>
        step.email_subject?.trim().length > 0 &&
        stripHtml(step.email_body || '').length > 0
    )

  if (isLoadingSequence) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="border-b border-line bg-surface px-8 py-6">
        <div className="mx-auto max-w-[1200px]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-brand">
                Step {stepNumber} of {totalSteps}
              </p>
              <h1 className="text-xl font-semibold text-fg">
                Build Your Sequence
              </h1>
              <p className="mt-1 text-sm text-fg-secondary">
                Create personalized emails with a live preview. Follow-up steps
                automatically default to a Re: subject based on step one.
              </p>
            </div>
            <div className="pt-0.5 text-xs text-fg-secondary">
              {readOnly
                ? 'Read-only'
                : saveStatus === 'saving'
                  ? 'Saving...'
                  : saveStatus === 'saved'
                    ? 'Saved'
                    : saveStatus === 'error'
                      ? 'Couldn’t save'
                      : saveStatus === 'unsaved'
                        ? 'Unsaved changes'
                        : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden xl:flex-row">
        <div className="flex-1 overflow-auto px-6 py-5">
          <div className="max-w-[700px]">
            {isLiveCampaign && !readOnly && (
              <div className="mb-4">
                <LiveCampaignBanner />
              </div>
            )}
            {readOnly && (
              <div className="mb-4 rounded-xl border border-line bg-surface-alt px-4 py-3 text-sm text-fg-secondary">
                This campaign has completed. The sequence is read-only.
              </div>
            )}
            {watchedSteps.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-brand/10">
                  <MessageSquare className="size-7 text-brand" />
                </div>
                <h2 className="mb-2 text-lg font-semibold text-fg">
                  Start building your sequence
                </h2>
                <p className="mx-auto mb-6 max-w-sm text-sm text-fg-secondary">
                  Add email steps to create your outreach sequence. Each step is
                  a personalized email sent at your chosen interval.
                </p>
                <FbButton
                  onClick={addStep}
                  className="gap-2"
                  disabled={readOnly}
                >
                  <Plus className="size-4" />
                  Add First Email Step
                </FbButton>
              </div>
            ) : (
              <div className={cn('space-y-0', readOnly && 'select-text')}>
                {fields.map((field, index) => {
                  const step = watchedSteps[index]
                  const isExpanded = expandedStepIndex === index
                  const isFirstStep = index === 0
                  const isAutoReplyStep = !isFirstStep
                  const stepSummary = stripHtml(step?.email_body || '')

                  return (
                    <div key={field.fieldKey}>
                      {index > 0 && (
                        <div className="flex items-center gap-3 py-3 pl-8">
                          <div className="ml-[3px] h-4 w-px bg-line" />
                          <div className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-xs text-fg-secondary">
                            <Clock className="size-3" />
                            <span>Wait</span>
                            <input
                              type="number"
                              min={1}
                              value={step.wait_in_days}
                              disabled={readOnly}
                              onChange={(event) =>
                                setValue(
                                  `steps.${index}.wait_in_days`,
                                  Math.max(1, Number(event.target.value) || 1),
                                  { shouldDirty: true, shouldValidate: true }
                                )
                              }
                              className="w-10 rounded bg-surface-alt px-1 py-0.5 text-center text-xs text-fg outline-none"
                            />
                            <span>days</span>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <div className="flex flex-col items-center pt-4">
                          <div
                            className={cn(
                              'flex size-8 shrink-0 items-center justify-center rounded-full text-sm',
                              step.email_subject &&
                                stripHtml(step.email_body || '')
                                ? 'bg-brand text-white'
                                : 'border-2 border-brand/30 bg-brand/10 text-brand'
                            )}
                          >
                            {index + 1}
                          </div>
                          {index < fields.length - 1 && !isExpanded && (
                            <div className="mt-2 w-px flex-1 bg-line" />
                          )}
                        </div>

                        <div className="mb-2 flex-1">
                          <div
                            className={cn(
                              'rounded-xl border bg-white transition-all',
                              isExpanded
                                ? 'border-brand/30 ring-2 ring-brand/15 shadow-sm'
                                : 'border-line hover:shadow-sm'
                            )}
                          >
                            <div
                              className="flex cursor-pointer items-center gap-3 px-4 py-3"
                              onClick={() =>
                                setExpandedStepIndex(isExpanded ? -1 : index)
                              }
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <GripVertical className="size-4 shrink-0 text-fg-secondary/35" />
                                  <p className="truncate text-sm text-fg">
                                    {step.email_subject?.trim() ? (
                                      step.email_subject
                                    ) : (
                                      <span className="italic text-fg-secondary">
                                        No subject line
                                      </span>
                                    )}
                                  </p>
                                </div>
                                {!isExpanded && stepSummary && (
                                  <p className="mt-0.5 truncate text-xs text-fg-secondary">
                                    {stepSummary}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  className="flex size-7 items-center justify-center rounded hover:bg-surface-alt text-fg-secondary"
                                  disabled={readOnly}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    duplicateStep(index)
                                  }}
                                  title="Duplicate step"
                                >
                                  <Copy className="size-3.5" />
                                </button>
                                {fields.length > 1 && (
                                  <button
                                    type="button"
                                    className="flex size-7 items-center justify-center rounded hover:bg-red-50 text-fg-secondary hover:text-red-600"
                                    disabled={readOnly}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      removeStepAtIndex(index)
                                    }}
                                    title="Remove step"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                )}
                                {isExpanded ? (
                                  <ChevronUp className="size-4 text-fg-secondary" />
                                ) : (
                                  <ChevronDown className="size-4 text-fg-secondary" />
                                )}
                              </div>
                            </div>

                            {isExpanded && (
                              <div
                                className={cn(
                                  'space-y-4 border-t border-line px-4 pb-4',
                                  readOnly && 'pointer-events-none'
                                )}
                              >
                                <div className="pt-4">
                                  <div className="mb-1.5 flex items-center gap-2">
                                    <label className="text-xs uppercase tracking-wider text-fg-secondary">
                                      Subject Line
                                    </label>
                                    {isAutoReplyStep && (
                                      <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xxs text-brand">
                                        Auto Re: from Step 1
                                      </span>
                                    )}
                                  </div>
                                  <BaseTextInput
                                    name={`steps.${index}.email_subject`}
                                    value={step.email_subject ?? ''}
                                    onChange={(value) => {
                                      if (index === 0) {
                                        handlePrimarySubjectChange(
                                          String(value)
                                        )
                                        return
                                      }
                                      setValue(
                                        `steps.${index}.email_subject`,
                                        String(value),
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        }
                                      )
                                    }}
                                    placeholder={
                                      isFirstStep
                                        ? 'Email subject line - use {FIRST_NAME}, {COMPANY}, etc.'
                                        : 'Re: [auto-generated from step 1]'
                                    }
                                    noMargin
                                  />
                                </div>

                                <div>
                                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-fg-secondary">
                                    Email Body
                                  </label>
                                  <EmailBodyEditor
                                    control={control}
                                    name={`steps.${index}.email_body`}
                                    label=""
                                    placeholder={
                                      'Write your email content here...\n\nUse variables like {FIRST_NAME} to personalize.'
                                    }
                                    hideHelperText
                                    disableToolbar={readOnly}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {!readOnly && (
                  <>
                    <div className="flex items-center gap-3 pt-3 pl-8">
                      <div className="ml-[3px] h-4 w-px bg-line" />
                    </div>
                    <div className="flex gap-3 pl-1">
                      <div className="flex flex-col items-center">
                        <div className="flex size-8 items-center justify-center rounded-full border-2 border-dashed border-brand/30">
                          <Plus className="size-4 text-brand/50" />
                        </div>
                      </div>
                      <FbButton
                        variant="outline"
                        onClick={addStep}
                        className="gap-2"
                      >
                        <Plus className="size-4" />
                        Add Email Step
                      </FbButton>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {watchedSteps.length > 0 && (
          <div className="flex w-full shrink-0 flex-col border-t border-line bg-surface-alt xl:w-[420px] xl:border-l xl:border-t-0">
            <div className="space-y-3 border-b border-line bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="size-4 text-brand" />
                  <span className="text-xs uppercase tracking-wider text-fg-secondary">
                    Live Preview
                  </span>
                </div>
                {previewWarnings.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xxs text-amber-700">
                    <AlertTriangle className="size-2.5" />
                    Unfilled variables
                  </span>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  disabled={
                    showNoIncludedContactsState || previewContacts.length <= 1
                  }
                  onClick={() => setShowContactDropdown((open) => !open)}
                  className="flex w-full items-center gap-2 rounded-lg bg-surface-alt px-2.5 py-1.5 text-left transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div className="flex size-6 items-center justify-center rounded-full bg-brand/10">
                    <User className="size-3 text-brand" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xxs uppercase tracking-wider text-fg-secondary">
                      Variables filled as
                    </p>
                    <p className="truncate text-xs text-fg">
                      {activePreviewContact?.label ??
                        (isUsingSamplePreview
                          ? 'Sample Contact'
                          : 'No included contacts')}
                      {(activePreviewContact?.subtitle ||
                        (isUsingSamplePreview
                          ? 'Example preview until campaign contacts are added'
                          : showNoIncludedContactsState
                            ? 'Go back and include at least one contact'
                            : 'Preview contact')) && (
                        <span className="ml-1 text-xs text-fg-secondary">
                          ·{' '}
                          {activePreviewContact?.subtitle ||
                            (isUsingSamplePreview
                              ? 'Example preview until campaign contacts are added'
                              : showNoIncludedContactsState
                                ? 'Go back and include at least one contact'
                                : 'Preview contact')}
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      'size-3.5 shrink-0 text-fg-secondary transition-transform',
                      showContactDropdown && 'rotate-180'
                    )}
                  />
                </button>
                {showContactDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowContactDropdown(false)}
                    />
                    <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-line bg-white py-1 shadow-lg">
                      <p className="px-3 py-1.5 text-xxs uppercase tracking-wider text-fg-secondary">
                        Preview As Contact
                      </p>
                      {previewContacts.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => {
                            setPreviewContactId(contact.id)
                            setShowContactDropdown(false)
                          }}
                          className={cn(
                            'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-surface-alt',
                            previewContactId === contact.id && 'bg-brand/5'
                          )}
                        >
                          <div className="flex size-6 items-center justify-center rounded-full bg-brand/10">
                            <User className="size-3 text-brand" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs text-fg">
                              {contact.label}
                            </p>
                            <p className="truncate text-xxs text-fg-secondary">
                              {contact.subtitle}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {watchedSteps.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewStepIndex((index) => Math.max(0, index - 1))
                    }
                    disabled={safePreviewStepIndex === 0}
                    className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronLeft className="size-3.5" />
                  </button>
                  <div className="flex flex-1 items-center justify-center gap-1.5">
                    {watchedSteps.map((_, index) => (
                      <button
                        key={`preview-step-${index}`}
                        type="button"
                        onClick={() => setPreviewStepIndex(index)}
                        className={cn(
                          'size-2 rounded-full transition-all',
                          index === safePreviewStepIndex
                            ? 'scale-125 bg-brand'
                            : 'bg-fg-secondary/20 hover:bg-fg-secondary/40'
                        )}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewStepIndex((index) =>
                        Math.min(watchedSteps.length - 1, index + 1)
                      )
                    }
                    disabled={safePreviewStepIndex === watchedSteps.length - 1}
                    className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronRight className="size-3.5" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto p-4">
              {showNoIncludedContactsState ? (
                <div className="flex h-full items-center justify-center">
                  <div className="max-w-xs rounded-xl border border-dashed border-line bg-white px-6 py-8 text-center">
                    <User className="mx-auto mb-3 size-8 text-fg-secondary/40" />
                    <h3 className="text-sm font-semibold text-fg">
                      No included preview contacts
                    </h3>
                    <p className="mt-2 text-sm text-fg-secondary">
                      Include at least one contact in Review Leads to preview
                      your sequence with real campaign recipients.
                    </p>
                  </div>
                </div>
              ) : (
                previewStep && (
                  <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b border-line bg-surface-alt px-4 py-2.5">
                      <div className="flex size-6 items-center justify-center rounded-full bg-brand text-xxs text-white">
                        {safePreviewStepIndex + 1}
                      </div>
                      <span className="text-xs text-fg-secondary">
                        Email Step {safePreviewStepIndex + 1} of{' '}
                        {watchedSteps.length}
                      </span>
                      {safePreviewStepIndex > 0 && (
                        <span className="ml-auto flex items-center gap-1 text-xxs text-fg-secondary">
                          <Clock className="size-2.5" />
                          {previewStep.wait_in_days} day
                          {previewStep.wait_in_days === 1 ? '' : 's'} after Step{' '}
                          {safePreviewStepIndex}
                        </span>
                      )}
                    </div>

                    <div className="border-b border-line px-4 py-3">
                      <p className="mb-1 text-xxs uppercase tracking-wider text-fg-secondary">
                        Subject
                      </p>
                      {filledPreviewSubject ? (
                        <p className="text-sm text-fg">
                          {filledPreviewSubject}
                        </p>
                      ) : (
                        <p className="text-sm italic text-fg-secondary">
                          No subject line
                        </p>
                      )}
                    </div>

                    <div className="px-4 py-4">
                      <p className="mb-2 text-xxs uppercase tracking-wider text-fg-secondary">
                        Body
                      </p>
                      {stripHtml(filledPreviewBody).length > 0 ? (
                        <div
                          className="prose prose-sm max-w-none break-words text-fg [overflow-wrap:anywhere]"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(filledPreviewBody),
                          }}
                        />
                      ) : (
                        <div className="py-8 text-center text-sm italic text-fg-secondary">
                          <MessageSquare className="mx-auto mb-2 size-6 opacity-30" />
                          No content yet - start typing in the editor
                        </div>
                      )}
                    </div>

                    {previewWarnings.length > 0 && (
                      <div className="border-t border-amber-100 bg-amber-50/50 px-4 py-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                          <div>
                            <p className="text-xs text-amber-700">
                              Some variables could not be filled for this
                              preview contact:
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {previewWarnings.map((warning) => (
                                <code
                                  key={warning}
                                  className="rounded bg-amber-100 px-1.5 py-0.5 text-xxs text-amber-700"
                                >
                                  {warning}
                                </code>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {previewStep &&
                activePreviewContact &&
                !showNoIncludedContactsState && (
                  <div className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-xs text-fg-secondary">
                    <p className="flex items-start gap-2 leading-relaxed">
                      <User className="mt-0.5 size-3.5 shrink-0 text-brand" />
                      Variables filled as{' '}
                      <span className="font-medium text-fg">
                        {activePreviewContact.label}
                      </span>
                      {activePreviewContact.subtitle
                        ? ` at ${activePreviewContact.subtitle}`
                        : '.'}
                    </p>
                    {isUsingSamplePreview && (
                      <p className="mt-1.5 flex items-start gap-2 leading-relaxed">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                        This is a sample preview. Add campaign contacts to see
                        real lead substitutions here.
                      </p>
                    )}
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-line bg-surface px-6 py-4">
        <div className="mx-auto flex max-w-[1200px] gap-3">
          <FbButton variant="outline" onClick={onBack} className="gap-2">
            <ChevronLeft className="size-4" />
            {backLabel}
          </FbButton>
          {!readOnly && (
            <FbButton
              onClick={() => void onSubmit()}
              disabled={!canProceed || isSaving}
              className="flex-1 gap-2"
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="size-4" />
                  Saving...
                </>
              ) : (
                <>
                  Continue to Settings
                  <ChevronRight className="size-4" />
                </>
              )}
            </FbButton>
          )}
        </div>
      </div>
    </div>
  )
}
